(function () {
  if (customElements.get('besties-banner')) return;

  const responseCache = (window.DearmoraBestiesCollectionCache =
    window.DearmoraBestiesCollectionCache || new Map());

  class BestiesBanner extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.configElement = this.querySelector('[data-besties-config]');
      this.tabs = Array.from(this.querySelectorAll('[data-besties-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-besties-panel]'));
      this.observer = null;

      if (!this.configElement || !this.tabs.length || !this.panels.length) return;

      try {
        this.config = JSON.parse(this.configElement.textContent);
      } catch (error) {
        return;
      }

      this.dataset.initialized = 'true';
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(tab));
        tab.addEventListener('keydown', (event) => this.onTabKeydown(event, index));
      });
      this.panels.forEach((panel) => this.setupPanel(panel));

      this.onResize = () => this.panels.forEach((panel) => this.updateLayout(panel));
      window.addEventListener('resize', this.onResize);

      const activePanel = this.panels.find((panel) => !panel.hidden);
      if (activePanel) this.observeInitialPanel(activePanel);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
    }

    observeInitialPanel(panel) {
      if (!('IntersectionObserver' in window)) {
        this.loadPanel(panel);
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.observer = null;
          this.loadPanel(panel);
        },
        { rootMargin: '300px 0px' }
      );

      this.observer.observe(this);
    }

    setupPanel(panel) {
      panel.querySelector('[data-besties-previous]')?.addEventListener('click', () => {
        this.move(panel, -1);
      });
      panel.querySelector('[data-besties-next]')?.addEventListener('click', () => {
        this.move(panel, 1);
      });
      panel.querySelector('[data-besties-track]')?.addEventListener(
        'scroll',
        () => this.updateLayout(panel),
        { passive: true }
      );
      panel.querySelector('[data-besties-retry]')?.addEventListener('click', () => {
        this.loadPanel(panel, true);
      });
    }

    selectTab(selectedTab) {
      this.observer?.disconnect();
      this.observer = null;

      this.tabs.forEach((tab) => {
        const active = tab === selectedTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });

      const panelId = selectedTab.getAttribute('aria-controls');
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });

      const panel = this.querySelector('#' + CSS.escape(panelId));
      if (!panel) return;

      const track = panel.querySelector('[data-besties-track]');
      if (track) track.scrollLeft = 0;
      this.loadPanel(panel);
    }

    onTabKeydown(event, index) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      let target = index;
      if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = this.tabs.length - 1;

      this.tabs[target].focus();
      this.selectTab(this.tabs[target]);
    }

    async loadPanel(panel, forceReload = false) {
      if (panel.dataset.loaded === 'true' && !forceReload) {
        this.updateLayout(panel);
        return;
      }
      if (panel.requestPromise && !forceReload) return panel.requestPromise;

      const handle = panel.dataset.collectionHandle || '';
      if (!handle) {
        this.renderMessage(
          panel,
          this.config.selectCollectionText || this.config.emptyText,
          'empty'
        );
        return;
      }

      if (forceReload) responseCache.delete(this.cacheKey(handle));
      this.renderLoading(panel);

      panel.requestPromise = this.fetchProducts(handle)
        .then((items) => this.renderProducts(panel, items))
        .catch(() => this.renderError(panel))
        .finally(() => {
          panel.requestPromise = null;
        });

      return panel.requestPromise;
    }

    cacheKey(handle) {
      return handle + ':ajax-collection-showcase-products';
    }

    fetchProducts(handle) {
      const key = this.cacheKey(handle);
      if (responseCache.has(key)) return responseCache.get(key);

      const rootUrl = this.normalizeRootUrl(this.config.rootUrl);
      const requestUrl =
        rootUrl +
        'collections/' +
        encodeURIComponent(handle) +
        '?section_id=ajax-collection-showcase-products';

      const request = fetch(requestUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      })
        .then((response) => {
          if (!response.ok) throw new Error('Collection request failed: ' + response.status);
          return response.text();
        })
        .then((html) => {
          const responseDocument = new DOMParser().parseFromString(html, 'text/html');
          const responseList = responseDocument.querySelector(
            '[data-ajax-collection-showcase-list]'
          );
          if (!responseList) throw new Error('Collection response markup is invalid');
          return Array.from(responseList.children).map((item) => item.outerHTML);
        })
        .catch((error) => {
          responseCache.delete(key);
          throw error;
        });

      responseCache.set(key, request);
      return request;
    }

    renderLoading(panel) {
      const track = panel.querySelector('[data-besties-track]');
      const status = panel.querySelector('[data-besties-status]');
      if (!track || !status) return;

      panel.dataset.state = 'loading';
      panel.setAttribute('aria-busy', 'true');
      panel.querySelector('[data-besties-retry]')?.setAttribute('hidden', '');
      status.hidden = false;
      status.textContent = this.config.loadingText;
      track.replaceChildren();

      const count = Math.min(
        Number(this.config.productsLimit) || 8,
        Number(this.config.desktopColumns) || 4
      );
      for (let index = 0; index < count; index += 1) {
        const skeleton = document.createElement('li');
        skeleton.className = 'besties-banner__skeleton';
        skeleton.setAttribute('aria-hidden', 'true');
        skeleton.innerHTML =
          '<span class="besties-banner__skeleton-media"></span>' +
          '<span class="besties-banner__skeleton-line"></span>' +
          '<span class="besties-banner__skeleton-line besties-banner__skeleton-line--short"></span>';
        track.appendChild(skeleton);
      }
      this.disableArrows(panel);
    }

    renderProducts(panel, itemMarkup) {
      const track = panel.querySelector('[data-besties-track]');
      const status = panel.querySelector('[data-besties-status]');
      if (!track || !status) return;

      const fragment = document.createDocumentFragment();
      itemMarkup.slice(0, Number(this.config.productsLimit) || 8).forEach((html, index) => {
        const template = document.createElement('template');
        template.innerHTML = html.trim();
        const item = template.content.firstElementChild;
        if (!item) return;

        item.classList.add('besties-banner__product');
        item.classList.remove('ajax-collection-showcase__product-item');
        this.scopeCardIds(item, panel, index);
        fragment.appendChild(item);
      });

      if (!fragment.childElementCount) {
        this.renderMessage(panel, this.config.emptyText, 'empty');
        return;
      }

      track.replaceChildren(fragment);
      status.hidden = true;
      status.textContent = '';
      panel.dataset.loaded = 'true';
      panel.dataset.state = 'loaded';
      panel.setAttribute('aria-busy', 'false');
      this.updateLayout(panel);

      this.dispatchEvent(
        new CustomEvent('besties-banner:rendered', {
          bubbles: true,
          detail: {
            collectionUrl: panel.dataset.collectionUrl || '',
            itemCount: track.children.length
          }
        })
      );
    }

    scopeCardIds(item, panel, index) {
      const suffix = '-' + this.id + '-' + panel.id + '-' + index;
      const idMap = new Map();

      item.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.id;
        const newId = oldId + suffix;
        idMap.set(oldId, newId);
        element.id = newId;
      });

      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attribute) => {
        item.querySelectorAll('[' + attribute + ']').forEach((element) => {
          const value = element.getAttribute(attribute);
          if (!value) return;
          element.setAttribute(
            attribute,
            value
              .split(/\s+/)
              .map((token) => idMap.get(token) || token)
              .join(' ')
          );
        });
      });
    }

    renderMessage(panel, message, state) {
      const track = panel.querySelector('[data-besties-track]');
      const status = panel.querySelector('[data-besties-status]');
      if (!track || !status) return;

      track.replaceChildren();
      status.hidden = false;
      status.textContent = message;
      panel.dataset.state = state;
      panel.setAttribute('aria-busy', 'false');
      delete panel.dataset.loaded;
      this.disableArrows(panel);
    }

    renderError(panel) {
      this.renderMessage(panel, this.config.errorText, 'error');
      const retry = panel.querySelector('[data-besties-retry]');
      if (!retry) return;
      retry.hidden = false;
      retry.textContent = this.config.retryText;
    }

    disableArrows(panel) {
      const previous = panel.querySelector('[data-besties-previous]');
      const next = panel.querySelector('[data-besties-next]');
      if (previous) previous.disabled = true;
      if (next) next.disabled = true;
    }

    move(panel, direction) {
      const track = panel.querySelector('[data-besties-track]');
      if (!track) return;

      const offsets = this.getScrollOffsets(track);
      if (offsets.length < 2) return;

      const currentIndex = offsets.reduce((closestIndex, offset, index) => {
        return Math.abs(offset - track.scrollLeft) < Math.abs(offsets[closestIndex] - track.scrollLeft)
          ? index
          : closestIndex;
      }, 0);
      const targetIndex = Math.min(
        offsets.length - 1,
        Math.max(0, currentIndex + direction)
      );

      track.scrollTo({ left: offsets[targetIndex], behavior: 'smooth' });
    }

    getScrollOffsets(track) {
      const products = Array.from(track.querySelectorAll('.besties-banner__product'));
      const trackRect = track.getBoundingClientRect();
      const maxLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      const paddingStart = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      const offsets = products
        .map((product) => {
          const productRect = product.getBoundingClientRect();
          const naturalLeft = productRect.left - trackRect.left + track.scrollLeft - paddingStart;
          return Math.min(maxLeft, Math.max(0, naturalLeft));
        })
        .sort((first, second) => first - second);

      return offsets.filter((offset, index) => {
        return index === 0 || Math.abs(offset - offsets[index - 1]) > 1;
      });
    }

    updateLayout(panel) {
      const track = panel.querySelector('[data-besties-track]');
      const previous = panel.querySelector('[data-besties-previous]');
      const next = panel.querySelector('[data-besties-next]');
      if (!track) return;

      const products = Array.from(track.querySelectorAll('.besties-banner__product'));
      if (!products.length) {
        this.disableArrows(panel);
        return;
      }

      products.forEach((product) => {
        product.hidden = false;
      });
      if (!previous || !next) return;
      const firstCard = products[0];
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      const firstStep = firstCard ? firstCard.getBoundingClientRect().width + gap : 0;
      previous.disabled = track.scrollLeft <= Math.max(2, firstStep / 2);
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }

    normalizeRootUrl(rootUrl) {
      const value = rootUrl || '/';
      return value.endsWith('/') ? value : value + '/';
    }
  }

  customElements.define('besties-banner', BestiesBanner);
})();
